// Platform-admin card: list all agent templates + quick actions.
//
// Shows status pills, version number, template type. Opens the
// editor dialog on "Edit" and the compile preview on "Preview".

import React, { useEffect, useState } from 'react';
import { BookOpen, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { templatesApi } from '../../../api';
import { TEMPLATE_TYPES, STATUS_BADGE } from './constants';
import TemplateEditorDialog from './TemplateEditorDialog';
import CompilePreviewDialog from './CompilePreviewDialog';

const EMPTY_FORM = {
  name: '',
  template_type: 'local_retail_appointment',
  description: '',
};

export default function AgentTemplatesCard() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setTemplates(await templatesApi.list());
    } catch (err) {
      toast.error(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await templatesApi.create(form);
      toast.success(`Created ${form.name}`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const editing = templates.find((t) => t.id === editingId) || null;

  return (
    <Card className="glass-card border-white/20" data-testid="agent-templates-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          <div>
            <CardTitle>Agent Templates</CardTitle>
            <CardDescription className="mt-1">
              Admin-owned agent brain. One compiled brief per (dealer, template, context).
            </CardDescription>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="w-4 h-4" /> New Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create agent template</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={form.template_type}
                    onValueChange={(v) => setForm((p) => ({ ...p, template_type: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description (shown on the dealer's agent card)</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={create} disabled={saving}>
                  {saving ? 'Creating…' : 'Create draft'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="text-muted-foreground text-sm">
            No templates yet. Run <code>python -m scripts.seed_agent_templates</code>
            {' '}to load the five defaults, or click <strong>New Template</strong>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-2">Name</th>
                  <th className="p-2">Type</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Version</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id} className="border-t border-white/10">
                    <td className="p-2 font-medium">{t.name}</td>
                    <td className="p-2 text-muted-foreground">{t.template_type}</td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[t.status] || ''}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="p-2">v{t.version}</td>
                    <td className="p-2 text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingId(t.id)}>
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPreviewId(t.id)}>
                        Preview
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <TemplateEditorDialog
        template={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditingId(null)}
        onChanged={refresh}
      />

      <CompilePreviewDialog
        open={!!previewId}
        onOpenChange={(o) => !o && setPreviewId(null)}
        defaultTemplateId={previewId}
      />
    </Card>
  );
}
